import Check from "@mui/icons-material/Check";
import { Box, Chip, Popover, Stack, useTheme } from "@mui/material";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { TaskStatus } from "generated-graphql/graphql";
import { useCallback, useMemo } from "react";
import { Control, Controller } from "react-hook-form";

import { taskStatusToChipSx, taskStatusToIcon } from "./statusStyles";
import { TaskFormType } from "./types";
import { taskStatusToLabel } from "./util.labels";

type StatusPickerProps = {
  control: Control<TaskFormType>;
  onOpenStatusPopover: (e: React.MouseEvent<HTMLElement>) => void;
  statusPopoverAnchorEl: HTMLElement | null;
  setStatusPopoverAnchorEl: (anchorEl: HTMLElement | null) => void;
};
export const StatusPicker = ({
  control,
  onOpenStatusPopover,
  statusPopoverAnchorEl,
  setStatusPopoverAnchorEl,
}: StatusPickerProps) => {
  const theme = useTheme();

  const allowedStatusOptions = useMemo(() => {
    return [TaskStatus.Open, TaskStatus.InProgress, TaskStatus.Completed, TaskStatus.Canceled];
  }, []);

  const handleStatusChange = useCallback(
    (s: TaskStatus, status: TaskStatus, onChange: () => void) => {
      if (s !== status) {
        onChange();
      }
      setStatusPopoverAnchorEl(null);
    },
    [setStatusPopoverAnchorEl]
  );

  return (
    <Controller
      name="taskStatus"
      control={control}
      render={({ field }) => {
        if (!field.value) return <></>;
        const status = field.value;
        const stylingBlock = taskStatusToChipSx(status, theme);
        return (
          <>
            <Chip
              icon={taskStatusToIcon(status)}
              label={taskStatusToLabel(status)}
              sx={{
                ...stylingBlock,
                "& .MuiChip-icon": { fontSize: 14 },
                cursor: "pointer",
              }}
              deleteIcon={<ArrowDropDownIcon />}
              onDelete={onOpenStatusPopover}
              slotProps={{
                root: { onClick: onOpenStatusPopover },
              }}
              size="small"
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
            <Popover
              open={Boolean(statusPopoverAnchorEl)}
              anchorEl={statusPopoverAnchorEl}
              onClose={() => setStatusPopoverAnchorEl(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              sx={{
                "& .MuiPaper-root": { p: 1, paddingLeft: 0 },
              }}
            >
              <Stack spacing={1} sx={{ p: 1 }}>
                {allowedStatusOptions.map((s) => {
                  const chipSx = {
                    ...taskStatusToChipSx(s, theme),
                    "& .MuiChip-icon": { fontSize: 16 },
                  };
                  const isCurrentStatus = s === status;
                  return (
                    <Stack
                      key={s}
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{
                        cursor: "pointer",
                      }}
                      onClick={() => handleStatusChange(s, status, () => field.onChange(s))}
                    >
                      {isCurrentStatus ? (
                        <Check sx={{ fontSize: 16 }} />
                      ) : (
                        <Box sx={{ width: 16 }} />
                      )}

                      <Chip
                        icon={taskStatusToIcon(s)}
                        label={taskStatusToLabel(s)}
                        sx={chipSx}
                        size="small"
                      />
                    </Stack>
                  );
                })}
              </Stack>
            </Popover>
          </>
        );
      }}
    />
  );
};
